import { useId, useState, type PointerEvent, type ReactNode } from 'react';
import { Text } from '../../atoms/Text/Text';
import { Tooltip } from '../../atoms/Tooltip/Tooltip';
import styles from './TableCell.module.css';

/**
 * Figma: `Table cell` (323:6430) — the body cell. 44px tall, `space/12` padding,
 * `border/primary` on the right and bottom edges only.
 *
 * `Type` is nine content shapes rather than nine appearances: the surface,
 * height, padding and borders are identical across all of them, and the only
 * thing that actually varies is the gap between the items inside. So `type`
 * survives as a prop — it is the one thing a caller cannot infer — while the
 * content itself comes through `children`, which is what a React table wants
 * anyway. Figma needs nine variants because an instance cannot take arbitrary
 * children; this does not.
 *
 * `Size` has a single option (`sm`) and does not become a prop.
 */
export type TableCellType =
  | 'text'
  | 'badge'
  | 'badgesMultiple'
  | 'trendPositive'
  | 'trendNegative'
  | 'selectDropdown'
  | 'progressBar'
  | 'actionButtons'
  | 'actionIcons';

export type TableCellProps = {
  /**
   * Render the lead text as the **row's link** — Figma's hovered row in
   * Competitive page performance (2024:32704, drawn 2026-09-21): on
   * `State=Hover/selected` the leading cell's text turns `text/link` and
   * underlined. The component's own hover variant only changes the surface;
   * this is that instance's override, as a prop.
   *
   * The text sits in a button so the row's action has a keyboard stop; the
   * link look shows on row hover and on the button's focus. Set by `Table` for
   * the first column when a row has `onActivate`, rather than by callers.
   */
  rowLink?: boolean;
  /**
   * The row link's accessible name, where its visible text alone does not say
   * what it opens — "Grameenphone" twice down a column is two different pages.
   * Not in Figma.
   */
  rowLinkLabel?: string;
  /** Figma `Type` — selects the gap Figma binds for that content shape. */
  type?: TableCellType;
  /** Figma `Lead action` — the row's checkbox, radio or toggle. */
  leadAction?: ReactNode;
  /** Figma `Lead text` — the primary label, used by `type="text"`. */
  text?: string;
  /** Figma `Supporting text` — the second line under `text`. */
  supportingText?: string;
  /** Content for every type that is not plain text. */
  children?: ReactNode;
  /** Figma `State=Hover/selected`. Hover itself is CSS; this is selection. */
  selected?: boolean;
  /**
   * The lead text is shown whole, on one line, instead of truncating. Set by
   * `Table` for a `fit` column. Not in Figma.
   */
  fit?: boolean;
  /**
   * This cell gives way to its `fit` neighbours — it asks for no width of its own
   * so a long text truncates rather than widening the grid. Set by `Table`.
   */
  fill?: boolean;
  /**
   * On hover, a tooltip shows the whole lead text — but only when the cell has
   * clipped it. Set by `Table` for a `reveal` column. Not in Figma.
   */
  reveal?: boolean;
  className?: string;
};

/**
 * Figma binds a different `itemSpacing` per content shape. Two of them point at
 * variables from a deleted library (`Action buttons` at 6, `Progress bar` at
 * 12); the local `space/*` tokens carrying those values are used instead — see
 * plan.md §7.
 */
const gapClass: Record<TableCellType, string> = {
  text: styles.gap12,
  badge: styles.gap0,
  badgesMultiple: styles.gap0,
  selectDropdown: styles.gap0,
  trendPositive: styles.gap6,
  trendNegative: styles.gap6,
  actionButtons: styles.gap6,
  actionIcons: styles.gap4,
  /*
   * Figma's `Type=Progress bar` cell embeds a `Progress Bar` at `Size=sm` — 128
   * wide against `lg`'s 256 — which is what makes the cell 152 across at 12px
   * padding. The bar itself arrives as `children`, so pass `size="sm"` with it;
   * this map only owns the gap.
   */
  progressBar: styles.gap12,
};

/** `Tooltip`'s own cap, mirrored so the bubble can be kept inside the window. */
const TOOLTIP_MAX = 280;
const TOOLTIP_GUTTER = 8;

export function TableCell({
  type = 'text',
  leadAction,
  text,
  supportingText,
  rowLink = false,
  rowLinkLabel,
  children,
  selected = false,
  fit = false,
  fill = false,
  reveal = false,
  className,
}: TableCellProps) {
  const tooltipId = useId();
  const [revealed, setRevealed] = useState<{ box: DOMRect; above: boolean } | null>(null);

  /* Open only for text the cell has actually cut off — a short one has nothing more to say. */
  const openReveal = (event: PointerEvent<HTMLElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    const clipped = [...event.currentTarget.children].some((el) => el.scrollWidth > el.clientWidth);
    /* The half of the window the text is in decides which side has room. */
    if (clipped) setRevealed({ box, above: box.top > window.innerHeight / 2 });
  };

  /* Figma `Lead text`. Plain `text/primary` at rest; see `rowLink` for its other state. */
  const leadText = text !== undefined && (
    <Text size="sm" leading="none" weight="regular" color="primary" truncate={!fit}>
      {text}
    </Text>
  );
  const lead =
    leadText && reveal && !rowLink ? (
      <span
        className={styles.reveal}
        aria-describedby={revealed ? tooltipId : undefined}
        onPointerEnter={openReveal}
        onPointerLeave={() => setRevealed(null)}
      >
        {leadText}
        {revealed && (
          <span
            className={styles.revealAnchor}
            style={{
              left: Math.min(revealed.box.left, window.innerWidth - TOOLTIP_MAX - TOOLTIP_GUTTER),
              top: revealed.box.top,
              width: revealed.box.width,
              height: revealed.box.height,
            }}
          >
            <Tooltip
              id={tooltipId}
              className={`${styles.revealBubble} ${revealed.above ? styles.above : styles.below}`}
            >
              {text}
            </Tooltip>
          </span>
        )}
      </span>
    ) : (
      leadText
    );

  return (
    <td
      className={[
        styles.cell,
        selected ? styles.selected : null,
        fit ? styles.fit : null,
        fill ? styles.fill : null,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      /*
       * Not in Figma — a selected row has to say so, not just look shaded.
       * The row carries `aria-selected`; the cell only reports its own state
       * when it is the thing that was selected.
       */
      data-selected={selected || undefined}
    >
      <span className={`${styles.content} ${gapClass[type]}`}>
        {leadAction !== undefined && <span className={styles.leadAction}>{leadAction}</span>}

        {(text !== undefined || supportingText !== undefined) && (
          /* Figma `Text and supporting text` — a `space/4` vertical stack. */
          <span className={styles.textStack}>
            {lead &&
              (rowLink ? (
                /*
                 * A real button, so the row's action is reachable by keyboard —
                 * a clickable `<tr>` on its own is not. It has no handler of
                 * its own: its click bubbles to the row, which `Table` owns and
                 * which runs the same action for a click anywhere across it.
                 */
                <button
                  type="button"
                  className={styles.rowLink}
                  data-row-link=""
                  aria-label={rowLinkLabel}
                >
                  {lead}
                </button>
              ) : (
                lead
              ))}
            {supportingText !== undefined && (
              <Text size="sm" leading="none" weight="regular" color="tertiary" truncate>
                {supportingText}
              </Text>
            )}
          </span>
        )}

        {children}
      </span>
    </td>
  );
}
